import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../app/store";

export const BANKS_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

export interface BankListItem {
  _id: string;
  id?: string;
  name?: string;
  name_ar?: string;
  status?: boolean;
  createdAt?: string;
  [key: string]: unknown;
}

export interface BanksState {
  list: BankListItem[];
  lastFetchedAt: number | null;
}

const initialState: BanksState = {
  list: [],
  lastFetchedAt: null,
};

export const banksSlice = createSlice({
  name: "banks",
  initialState,
  reducers: {
    setBanks: (
      state,
      action: PayloadAction<{ list: BankListItem[]; lastFetchedAt: number }>
    ) => {
      state.list = action.payload.list;
      state.lastFetchedAt = action.payload.lastFetchedAt;
    },
    addBank: (state, action: PayloadAction<BankListItem>) => {
      const item = {
        ...action.payload,
        id: action.payload._id ?? action.payload.id,
      };
      state.list.push(item);
    },
    updateBank: (
      state,
      action: PayloadAction<{ id: string; data: Partial<BankListItem> }>
    ) => {
      const index = state.list.findIndex((b) => b._id === action.payload.id);
      if (index !== -1) {
        state.list[index] = {
          ...state.list[index],
          ...action.payload.data,
          _id: action.payload.id,
          id: action.payload.id,
        };
      }
    },
    removeBank: (state, action: PayloadAction<string>) => {
      state.list = state.list.filter((b) => b._id !== action.payload);
    },
    clearBanks: (state) => {
      state.list = [];
      state.lastFetchedAt = null;
    },
  },
});

export const {
  setBanks,
  addBank,
  updateBank,
  removeBank,
  clearBanks,
} = banksSlice.actions;

export const selectBanksList = (state: RootState) => state.banks.list;
export const selectBanksLastFetchedAt = (state: RootState) =>
  state.banks.lastFetchedAt;
export const selectIsBanksCacheValid = (state: RootState): boolean => {
  const last = state.banks.lastFetchedAt;
  if (last == null) return false;
  return Date.now() - last < BANKS_CACHE_TTL_MS;
};

export default banksSlice.reducer;
